<template>
    <section class="tk-engagements-panel">
        <div class="tk-engagements-panel__header">
            <h2>Te Kohinga Client List</h2>
            <q-btn
                color="primary"
                no-caps
                unelevated
                icon="add"
                label="Create Client Engagement"
                :disable="!filters.service"
                @click="openCreate"
            />
        </div>

        <div class="tk-engagements-filters row q-col-gutter-md">
                    <div class="col-12 col-lg-3">
                        <q-select
                            v-model="filters.service"
                            :options="serviceOptions"
                            outlined
                            dense
                            emit-value
                            map-options
                            label="Service"
                            @update:model-value="onServiceChange"
                        />
                    </div>
                    <div class="col-12 col-lg-3">
                        <q-input
                            v-model="filters.q"
                            outlined
                            dense
                            clearable
                            debounce="300"
                            label="Search Client Engagements"
                            placeholder="Name, email, NHI or phone number"
                            @update:model-value="refresh"
                        >
                            <template v-slot:prepend>
                                <q-icon name="search" />
                            </template>
                        </q-input>
                    </div>
                    <div class="col-12 col-lg-3">
                        <q-select
                            v-model="filters.status"
                            :options="statusOptions"
                            multiple
                            outlined
                            dense
                            clearable
                            emit-value
                            map-options
                            label="Status"
                            @update:model-value="refresh"
                        />
                    </div>
                    <div class="col-12 col-lg-3">
                        <q-select
                            v-model="filters.assignedWorker"
                            :options="staffOptions"
                            outlined
                            dense
                            clearable
                            emit-value
                            map-options
                            label="Primary Worker"
                            @update:model-value="refresh"
                        />
                    </div>
        </div>

        <TkEngagementTable
            :rows="rows"
            :loading="loading"
            variant="service"
            no-data-message="No Client Engagements found for this service."
            @open="openEngagement"
        />
    </section>
</template>

<script>
import api from "@/services/api/api";
import notify from "@/services/util/notify";
import TkEngagementTable from "@/components/TkClients/TkEngagementTable.vue";
import { mapEngagementRow, staffOption } from "@/services/util/tkClients";

export default {
    name: "TkServiceDashboard",
    components: { TkEngagementTable },
    data() {
        return {
            loading: true,
            rows: [],
            lookups: { services: [], statuses: [] },
            staff: [],
            filters: {
                service: null,
                q: "",
                status: ["waitlist", "active"],
                assignedWorker: null,
            },
        };
    },
    computed: {
        serviceOptions() {
            return this.lookups.services.map(service => ({ label: service.name, value: service.code }));
        },
        statusOptions() {
            return this.lookups.statuses.map(status => ({ label: status.name, value: status.code }));
        },
        staffOptions() {
            return this.staff.map(staffOption);
        },
    },
    async mounted() {
        await Promise.all([this.loadLookups(), this.loadStaff()]);
        this.filters.service = this.$route.query.service || this.lookups.services[0]?.code || null;
        await this.refresh();
    },
    methods: {
        async loadLookups() {
            try {
                const res = await api.tkPeople.getEngagementLookups();
                this.lookups = res.data;
                this.filters.status = this.lookups.statuses
                    .filter(status => status.code !== "exited")
                    .map(status => status.code);
            } catch (error) {
                notify.error("Failed to load Client Engagement options", "top");
            }
        },
        async loadStaff() {
            try {
                const res = await api.users.getAppStaffList();
                this.staff = res.data || [];
            } catch (error) {
                notify.error("Failed to load staff options", "top");
            }
        },
        async refresh() {
            if (!this.filters.service) {
                this.rows = [];
                this.loading = false;
                return;
            }

            this.loading = true;
            try {
                const res = await api.tkPeople.listServiceEngagements({
                    service: this.filters.service,
                    q: this.filters.q || undefined,
                    status: this.filters.status?.join(",") || undefined,
                    assigned_worker: this.filters.assignedWorker || undefined,
                    limit: 200,
                });
                this.rows = (res.data || []).map(mapEngagementRow);
            } catch (error) {
                notify.error("Failed to load Client Engagements for this service", "top");
            } finally {
                this.loading = false;
            }
        },
        async onServiceChange(value) {
            await this.$router.replace({
                name: "tkServiceDashboard",
                query: value ? { service: value } : {},
            });
            await this.refresh();
        },
        openCreate() {
            this.$router.push({
                name: "tkCreateEngagement",
                query: this.filters.service ? { service: this.filters.service } : {},
            });
        },
        openEngagement(row) {
            this.$router.push({
                name: "tkEngagementDetail",
                params: { personId: row.tk_person_id, engagementId: row.id },
            });
        },
    },
};
</script>
